/**
 * Diff raso (por caminho) entre dois valores JSON — usado pelo ajuste de
 * regra (`TP-050`, T9) para mostrar o que mudou antes de confirmar (D6: o
 * ajuste é sempre `escopo`/`efeito` novo, nunca o trecho).
 *
 * Deliberadamente simples: não é um algoritmo de diff textual, é uma
 * COMPARAÇÃO DE CAMPO que lista todo caminho cujo valor final (folha) mudou
 * — suficiente para a pessoa conferir o que ela está prestes a gravar, sem
 * trazer uma biblioteca nova (fora do que o tech-plan aprovou).
 */

export interface DiferencaDeCampo {
  readonly caminho: string;
  readonly antes: unknown;
  readonly depois: unknown;
}

function ehObjetoPlano(valor: unknown): valor is Record<string, unknown> {
  return typeof valor === 'object' && valor !== null && !Array.isArray(valor);
}

function coletarCaminhos(valor: unknown, prefixo: string, destino: Set<string>): void {
  if (ehObjetoPlano(valor)) {
    for (const chave of Object.keys(valor)) {
      coletarCaminhos(valor[chave], prefixo === '' ? chave : `${prefixo}.${chave}`, destino);
    }
    return;
  }
  destino.add(prefixo);
}

function lerCaminho(valor: unknown, caminho: string): unknown {
  return caminho.split('.').reduce<unknown>((atual, chave) => {
    if (!ehObjetoPlano(atual)) return undefined;
    return atual[chave];
  }, valor);
}

/** Lista as folhas (campos escalares ou array) cujo valor mudou, por igualdade estrutural (JSON). */
export function diferencasDeCampos(antes: unknown, depois: unknown): DiferencaDeCampo[] {
  const caminhos = new Set<string>();
  coletarCaminhos(antes, '', caminhos);
  coletarCaminhos(depois, '', caminhos);
  caminhos.delete('');

  const diferencas: DiferencaDeCampo[] = [];
  for (const caminho of caminhos) {
    const valorAntes = lerCaminho(antes, caminho);
    const valorDepois = lerCaminho(depois, caminho);
    if (JSON.stringify(valorAntes) !== JSON.stringify(valorDepois)) {
      diferencas.push({ caminho, antes: valorAntes, depois: valorDepois });
    }
  }
  return diferencas.sort((a, b) => a.caminho.localeCompare(b.caminho));
}
